import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import { NavLink } from "@/components/NavLink";
import { MapPin, ArrowLeft } from "lucide-react";

const GoogleBusinessProfileForLawnCare = () => {
  return (
    <>
      <SEO 
        title="Google Business Profile for Lawn Care: Complete Setup Guide"
        description="Learn how to optimize your Google Business Profile to get more local lawn care customers. Step-by-step guide for lawn care and landscaping businesses."
        keywords="google business profile lawn care, google my business lawn care, local SEO landscaping, lawn care local search"
        canonical="https://www.leafrushmarketing.com/resources/google-business-profile-for-lawn-care/"
        ogUrl="https://www.leafrushmarketing.com/resources/google-business-profile-for-lawn-care/"
      />
      <article className="flex flex-col">
        {/* Back Link */}
        <section className="py-6 border-b">
          <div className="container px-4 md:px-6">
            <NavLink to="/resources" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Resources
            </NavLink>
          </div>
        </section>

        {/* Article Header */}
        <section className="py-12 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto space-y-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-primary" />
                </div>
                <span className="text-sm font-medium text-primary">LOCAL SEO</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
                Google Business Profile for Lawn Care: Complete Setup Guide
              </h1>
              <p className="text-xl text-muted-foreground">
                Your Google Business Profile is the most powerful free marketing tool for local lawn care businesses. 
                Here's how to set it up and optimize it to show up in local searches and get more calls.
              </p>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="pb-12 md:pb-20">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto prose prose-lg">
              <h2 className="text-2xl md:text-3xl font-bold mt-8 mb-4">What is Google Business Profile?</h2>
              
              <p className="text-muted-foreground mb-6">
                Google Business Profile (formerly Google My Business) is the listing that appears when someone searches for 
                lawn care services in your area. It shows your business name, phone number, hours, reviews, photos, and more—right 
                at the top of Google search results and Google Maps.
              </p>

              <p className="text-muted-foreground mb-6">
                <strong>Why it matters:</strong> Most lawn care customers search locally. If your profile isn't claimed, optimized, 
                and active, you're invisible to potential customers in your area.
              </p>

              <h2 className="text-2xl md:text-3xl font-bold mt-12 mb-4">Step 1: Claim and Verify Your Profile</h2>
              
              <p className="text-muted-foreground mb-4">
                If you haven't already, go to <a href="https://business.google.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">business.google.com</a> and 
                claim your listing. Google will verify your business by sending a postcard with a code to your business address, 
                or sometimes via phone or email.
              </p>

              <p className="text-muted-foreground mb-6">
                <strong>Important:</strong> Make sure your business name, address, and phone number (NAP) match exactly 
                what's on your website and other directories.
              </p>

              <h2 className="text-2xl md:text-3xl font-bold mt-12 mb-4">Step 2: Complete Every Section of Your Profile</h2>
              
              <p className="text-muted-foreground mb-6">
                Google rewards complete, accurate profiles with better rankings. Don't leave anything blank.
              </p>

              <h3 className="text-xl font-semibold mt-8 mb-3">Business Information</h3>
              <ul className="space-y-2 text-muted-foreground mb-6">
                <li><strong>Business Name:</strong> Use your actual business name (don't stuff keywords here—Google penalizes that)</li>
                <li><strong>Category:</strong> Choose "Lawn Care Service" or "Landscaper" as your primary category</li>
                <li><strong>Service Area:</strong> List all cities/towns you serve (be specific)</li>
                <li><strong>Hours:</strong> Keep these updated, especially during off-season</li>
                <li><strong>Phone Number:</strong> Use a local number, not a toll-free one</li>
                <li><strong>Website:</strong> Link to your professional website (this is critical!)</li>
              </ul>

              <h3 className="text-xl font-semibold mt-8 mb-3">Business Description</h3>
              <p className="text-muted-foreground mb-6">
                Write a clear, 150-200 word description that explains what you do, who you serve, and what makes you different. 
                Mention your service area and key services naturally (e.g., "We provide lawn mowing, fertilization, and aeration 
                services to homeowners in [City] and surrounding areas").
              </p>

              <h2 className="text-2xl md:text-3xl font-bold mt-12 mb-4">Step 3: Add High-Quality Photos</h2>
              
              <p className="text-muted-foreground mb-4">
                Profiles with photos get 42% more requests for directions and 35% more clicks to their website. Upload:
              </p>

              <ul className="space-y-2 text-muted-foreground mb-6">
                <li><strong>Logo:</strong> Clean, professional business logo</li>
                <li><strong>Cover Photo:</strong> Eye-catching image of your best work</li>
                <li><strong>Before/After Photos:</strong> Show the transformation your services deliver</li>
                <li><strong>Team Photos:</strong> Build trust by showing who you are</li>
                <li><strong>Equipment Photos:</strong> Professional equipment signals quality</li>
              </ul>

              <p className="text-muted-foreground mb-6">
                Aim for at least 10-15 photos to start, and add new ones regularly (Google rewards fresh content).
              </p>

              <h2 className="text-2xl md:text-3xl font-bold mt-12 mb-4">Step 4: Get and Respond to Reviews</h2>
              
              <p className="text-muted-foreground mb-6">
                Reviews are the #1 ranking factor for local search. More 5-star reviews = higher rankings and more trust.
              </p>

              <h3 className="text-xl font-semibold mt-8 mb-3">How to Get More Reviews:</h3>
              <ul className="space-y-2 text-muted-foreground mb-6">
                <li>Ask satisfied customers in person after completing a job</li>
                <li>Send a follow-up text or email with a direct link to your review page</li>
                <li>Make it easy—provide the exact Google review link</li>
                <li>Be consistent—ask every satisfied customer, not just occasionally</li>
              </ul>

              <h3 className="text-xl font-semibold mt-8 mb-3">Respond to Every Review:</h3>
              <p className="text-muted-foreground mb-6">
                Thank customers for positive reviews and address negative ones professionally. Google sees engagement as a 
                positive signal, and potential customers notice when you care about feedback.
              </p>

              <h2 className="text-2xl md:text-3xl font-bold mt-12 mb-4">Step 5: Post Regular Updates</h2>
              
              <p className="text-muted-foreground mb-6">
                Use Google Posts to share updates, special offers, seasonal tips, or project highlights. Posts appear directly 
                in your profile and keep your listing fresh and active.
              </p>

              <p className="text-muted-foreground mb-6">
                <strong>Post ideas:</strong> Spring lawn prep tips, seasonal discounts, new service announcements, 
                before/after project showcases.
              </p>

              <h2 className="text-2xl md:text-3xl font-bold mt-12 mb-4">Step 6: Monitor and Improve</h2>
              
              <p className="text-muted-foreground mb-6">
                Check your Google Business Profile insights regularly to see:
              </p>

              <ul className="space-y-2 text-muted-foreground mb-6">
                <li>How many people found your business</li>
                <li>Which search terms they used</li>
                <li>How many clicked to your website or called you</li>
                <li>Where your profile views are coming from (Google Search vs. Maps)</li>
              </ul>

              <p className="text-muted-foreground mb-6">
                Use this data to refine your strategy and focus on what's working.
              </p>

              <h2 className="text-2xl md:text-3xl font-bold mt-12 mb-4">Need Help Optimizing Your Google Business Profile?</h2>
              
              <p className="text-muted-foreground mb-6">
                At Leaf Rush Marketing, we offer Google Business Profile optimization as part of our growth services. 
                We'll handle the setup, optimization, review generation strategy, and ongoing management so you can focus on running your business.
              </p>

              <p className="text-muted-foreground mb-8">
                We also build websites for lawn care and landscaping businesses that integrate seamlessly with your Google profile 
                to maximize local search visibility and drive more leads.
              </p>

              <div className="not-prose">
                <NavLink to="/contact#get-started">
                  <Button size="lg" className="text-lg px-8">
                    Get Started
                  </Button>
                </NavLink>
              </div>
            </div>
          </div>
        </section>
      </article>
    </>
  );
};

export default GoogleBusinessProfileForLawnCare;
